import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  Badge,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  useTheme,
} from "@mui/material";
import { FilterAltOff as FilterAltOffIcon, OpenInNew } from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import moment from "moment";
import AppTable from "components/Shared/AppTable";
import AppTableDateTime from "components/Shared/AppTableDateTime";
import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import { useTablePagination, useDebounce } from "hooks";
import {
  useLazyListVendorPricingsQuery,
  useLazyGetVendorPricingSummaryQuery,
} from "store/services/vendorProfileApi";
import ClearableTextField from "components/Shared/ClearableTextField";
import ExportVendorPriceList from "./ExportVendorPriceList";

const VendorPriceList = () => {
  const { id } = useParams();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
    useTablePagination();
  const [paymentStatus, setPaymentStatus] = useState("All");
  const paymentStatusOptions = ["All", "Paid", "Not Paid", "Refunded"];

  // Custom date range state — only applied on "Apply"
  const [customFromDate, setCustomFromDate] = useState(null);
  const [customToDate, setCustomToDate] = useState(null);
  const [appliedFromDate, setAppliedFromDate] = useState(null);
  const [appliedToDate, setAppliedToDate] = useState(null);

  // Keyword search state — debounced (only triggers API when at least 4 chars)
  const [keywordInput, setKeywordInput] = useState("");
  const { debouncedValue: debouncedKeyword, isBusy: keywordTyping } = useDebounce(
    keywordInput.trim(),
    750
  );
  const [appliedKeyword, setAppliedKeyword] = useState("");

  // Apply debounced keyword — only if it meets the 4-character minimum
  useEffect(() => {
    if (debouncedKeyword.length === 0) {
      if (appliedKeyword !== "") {
        setAppliedKeyword("");
        handlePageChange(1);
      }
      return;
    }
    if (debouncedKeyword.length < 4) return;
    if (debouncedKeyword !== appliedKeyword) {
      setAppliedKeyword(debouncedKeyword);
      handlePageChange(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedKeyword]);

  const handleClearKeyword = () => {
    setKeywordInput("");
    if (appliedKeyword !== "") {
      setAppliedKeyword("");
      handlePageChange(1);
    }
  };

  const isCustomRangeValid =
    customFromDate &&
    customToDate &&
    moment(customFromDate).isValid() &&
    moment(customToDate).isValid() &&
    moment(customToDate).isAfter(moment(customFromDate));

  const hasAppliedRange = !!(appliedFromDate && appliedToDate);

  const handleClearRange = () => {
    setCustomFromDate(null);
    setCustomToDate(null);
    setAppliedFromDate(null);
    setAppliedToDate(null);
    handlePageChange(1);
  };

  const conditionalRowStyles = [
    {
      when: (row) =>
        row?.paymentStatus === "Refunded" || (row?.charge != null && Number(row?.charge) < 0),
      style: {
        backgroundColor: isDark ? "rgba(211, 47, 47, 0.14)" : "#FFF5F5",
        borderLeft: isDark ? "4px solid #ef5350" : "4px solid #d32f2f",
        "&:hover": {
          backgroundColor: isDark
            ? "rgba(211, 47, 47, 0.24) !important"
            : "#FFEBEB !important",
        },
      },
    },
  ];

  const columns = [
    {
      name: "Shipment ID",
      selector: (row) => row?.shipment?.shipmentId || "N/A",
      cell: (row) => {
        const shipmentId = row?.shipment?.shipmentId || row?.shipmentId;
        const shipmentDbId =
          row?.shipment?._id ||
          row?.shipmentObjectId ||
          row?.shipmentId?._id ||
          (typeof row?.shipmentId === "string" ? null : row?.shipmentId);
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            {shipmentId || "N/A"}
            {shipmentDbId && (
              <Tooltip title="View shipment detail in a new tab">
                <IconButton
                  size="small"
                  onClick={() => window.open(`/app/crm/shipment/${shipmentDbId}/detail`, "_blank")}
                >
                  <OpenInNew fontSize="inherit" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        );
      },
      width: "180px",
    },
    {
      name: "Fee Item",
      selector: (row) => row?.feeItem?.name || "N/A",
      cell: (row) => row?.feeItem?.name || "N/A",
    },
    {
      name: "Unit",
      selector: (row) => row?.shipment?.weight || row?.shipment?.unit?.value,
      cell: (row) => {
        const s = row?.shipment;
        if (s?.weight) return `${s.weight.toFixed(2)} kg`;
        if (s?.unit?.value && s?.unit?.unitType?.name) {
          return s.unit.unitType.name.toLowerCase() === "weight"
            ? `${s.unit.value} kg`
            : `${s.unit.value} ${s.unit.unitType.name}`;
        }
        return "N/A";
      },
    },
    {
      name: "Charge",
      selector: (row) => row?.charge,
      cell: (row) => {
        const isRefunded =
          row?.paymentStatus === "Refunded" || (row?.charge != null && Number(row?.charge) < 0);
        const chargeNum = Number(row?.charge || 0);
        const formatted = isRefunded
          ? (chargeNum < 0 ? `Rs. ${chargeNum.toFixed(2)}` : `-Rs. ${chargeNum.toFixed(2)}`)
          : `Rs. ${chargeNum.toFixed(2)}`;
        return (
          <Typography
            component="span"
            variant="body2"
            sx={{
              color: isRefunded ? "error.main" : "text.primary",
              fontWeight: isRefunded ? 600 : 500,
            }}
          >
            {formatted}
          </Typography>
        );
      },
    },
    {
      name: "Charge Type",
      selector: (row) => row?.chargeType,
      center: true,
      cell: (row) => (
        <Badge
          className={`rounded-md px-1 py-1 ${row.chargeType ? "" : "bg-gray-500 text-white"
            }`}
        >
          {row.chargeType || "N/A"}
        </Badge>
      ),
    },
    {
      name: "Payer",
      selector: (row) => row?.payer || "N/A",
      cell: (row) => row?.payer || "N/A",
    },
    {
      name: "Payment Location",
      selector: (row) => row?.paymentLocation || "N/A",
      cell: (row) => row?.paymentLocation || "N/A",
    },
    {
      name: "Status",
      selector: (row) =>
        row?.paymentStatus || row?.discountStatus || row?.remitStatus || "N/A",
      center: true,
      cell: (row) => {
        const status =
          row?.paymentStatus ||
          row?.discountStatus ||
          row?.remitStatus ||
          "N/A";

        let bgcolor = "#F5F5F5";
        let color = "#616161";

        if (status === "Refunded") {
          bgcolor = "#FFEBEE";
          color = "#C62828";
        } else if (status === "Paid") {
          bgcolor = "#E8F5E9";
          color = "#2E7D32";
        } else if (status === "Not Paid") {
          bgcolor = "#FFF3E0";
          color = "#E65100";
        } else if (status === "Collected") {
          bgcolor = "#E0F2F1";
          color = "#00695C";
        }

        return (
          <Chip
            label={status}
            size="small"
            sx={{
              bgcolor,
              color,
              fontWeight: 600,
              fontSize: "0.75rem",
              height: "24px",
            }}
          />
        );
      },
    },
    {
      name: "Created At",
      selector: (row) => row?.createdAt,
      cell: (row) => <AppTableDateTime date={row?.createdAt} />,
    },
  ];

  const [fetchPricings, { data: pricingRes, isLoading, isFetching }] =
    useLazyListVendorPricingsQuery();

  const [fetchSummary, { data: summaryRes, isLoading: isSummaryLoading }] =
    useLazyGetVendorPricingSummaryQuery();

  useEffect(() => {
    if (id) {
      const fromDate = appliedFromDate
        ? moment(appliedFromDate).format("MM-DD-YYYY")
        : undefined;
      const toDate = appliedToDate
        ? moment(appliedToDate).format("MM-DD-YYYY")
        : undefined;
      fetchPricings({
        vendorId: id,
        page: currentPage,
        limit: perPage,
        paymentStatus: paymentStatus !== "All" ? paymentStatus : undefined,
        fromDate,
        toDate,
        keyword: appliedKeyword || undefined,
      });
    }
  }, [
    id,
    fetchPricings,
    currentPage,
    perPage,
    paymentStatus,
    appliedFromDate,
    appliedToDate,
    appliedKeyword,
  ]);

  useEffect(() => {
    if (id) {
      const fromDate = appliedFromDate
        ? moment(appliedFromDate).format("MM-DD-YYYY")
        : undefined;
      const toDate = appliedToDate
        ? moment(appliedToDate).format("MM-DD-YYYY")
        : undefined;
      fetchSummary({ vendorId: id, fromDate, toDate });
    }
  }, [id, fetchSummary, appliedFromDate, appliedToDate]);

  const summaryItems = [
    { label: "Total Amount", value: summaryRes?.total },
    { label: "Total Paid", value: summaryRes?.totalPaid },
    { label: "Total Unpaid", value: summaryRes?.totalUnpaid },
    ...(summaryRes?.totalRefunded != null && summaryRes?.totalRefunded !== 0
      ? [{ label: "Total Refunded", value: summaryRes?.totalRefunded, isRefunded: true }]
      : []),
  ];

  return (
    <BreadcrumbLayout.Paper>
      <Box display="flex" alignItems="center" justifyContent="space-between" pt={1} pb={2}>
        <Box display="flex" gap={1.5}>
          {summaryItems.map(({ label, value, isRefunded }) => (
            <Box
              key={label}
              display="flex"
              alignItems="baseline"
              gap={0.5}
              px={1.25}
              py={0.5}
              border={1}
              borderColor={isRefunded ? "error.light" : "grey.300"}
              bgcolor={
                isRefunded
                  ? isDark
                    ? "rgba(211, 47, 47, 0.12)"
                    : "#FFF5F5"
                  : "transparent"
              }
              borderRadius={1}
            >
              <Typography
                variant="caption"
                color={isRefunded ? "error.main" : "text.secondary"}
                whiteSpace="nowrap"
              >
                {label}:
              </Typography>
              <Typography
                variant="body2"
                fontWeight={600}
                color={isRefunded ? "error.main" : "text.primary"}
                whiteSpace="nowrap"
              >
                {isSummaryLoading
                  ? "…"
                  : isRefunded
                  ? `-Rs. ${Math.abs(value || 0).toFixed(2)}`
                  : `Rs. ${value?.toFixed(2) ?? "0.00"}`}
              </Typography>
            </Box>
          ))}
        </Box>

        <Box display="flex" alignItems="center" gap={1}>
          <ClearableTextField
            label="Search Shipment ID"
            placeholder="Search Shipment ID"
            value={keywordInput}
            onChange={(e) => setKeywordInput(e.target.value)}
            handleClear={handleClearKeyword}
            typing={keywordTyping}
            sx={{ minWidth: 220 }}
            size="small"
          />
          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Payment Status</InputLabel>
            <Select
              value={paymentStatus}
              label="Payment Status"
              onChange={(e) => {
                setPaymentStatus(e.target.value);
                handlePageChange(1); // Reset to first page on filter change
              }}
              size="small"
            >
              {paymentStatusOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Stack direction="row" spacing={1} alignItems="center">
              <DatePicker
                label="From"
                value={customFromDate}
                onChange={(newValue) => setCustomFromDate(newValue)}
                renderInput={(params) => (
                  <TextField {...params} size="small" sx={{ width: 140 }} />
                )}
                maxDate={customToDate || new Date()}
                disableFuture
              />
              <DatePicker
                label="To"
                value={customToDate}
                onChange={(newValue) => setCustomToDate(newValue)}
                renderInput={(params) => (
                  <TextField {...params} size="small" sx={{ width: 140 }} />
                )}
                minDate={customFromDate}
                maxDate={new Date()}
                disableFuture
              />
              <Tooltip
                title={
                  isCustomRangeValid
                    ? "Apply date range"
                    : "Select a valid range"
                }
              >
                <span>
                  <Button
                    variant="contained"
                    size="small"
                    disabled={!isCustomRangeValid}
                    onClick={() => {
                      setAppliedFromDate(customFromDate);
                      setAppliedToDate(customToDate);
                      handlePageChange(1);
                    }}
                  >
                    Apply
                  </Button>
                </span>
              </Tooltip>
              {hasAppliedRange ? (
                <Button
                  variant="outlined"
                  size="small"
                  color="inherit"
                  onClick={handleClearRange}
                >
                  Clear
                </Button>
              ) : (
                <Tooltip title="No date range filter applied">
                  <span>
                    <IconButton size="small" disabled>
                      <FilterAltOffIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
            </Stack>
          </LocalizationProvider>
          <ExportVendorPriceList vendorId={id} />
        </Box>
      </Box>

      {pricingRes ? (
        <AppTable
          columns={columns}
          data={pricingRes?.data || []}
          progressPending={isLoading || isFetching}
          paginationServer
          paginationPerPage={perPage}
          paginationTotalRows={pricingRes?.totalItems || 0}
          paginationDefaultPage={currentPage}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
          conditionalRowStyles={conditionalRowStyles}
        />
      ) : (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          padding={2}
        >
          <CircularProgress size={24} />
          <Typography variant="body2" marginLeft={1}>
            Loading Pricing...
          </Typography>
        </Box>
      )}
    </BreadcrumbLayout.Paper>
  );
};

export default VendorPriceList;